/**
 * Pure list-patch logic for SportsnewContext, extracted to a plain .js module
 * (no JSX/React imports) so it can be unit-tested directly with Node instead
 * of only via source-pattern regex matching.
 */

/* ── Canonical fixture ID — always a String so Set/Map keying is type-safe ── */
export const fxId = (f) => {
  const id = f.fixtureId ?? f.id;
  return id != null ? String(id) : null;
};

/* ── Merge one mainOdds slot (home/draw/away), never letting a leaner
   incoming tick blow away stable identity fields (canonicalSelectionKey/
   canonicalLineKey/canonicalPairKey/selectionLine/groupingKey/shortOddId)
   that only the initial REST fetch carried. A price/state tick only needs to
   overlay the fields it actually sends. ── */
const mergeMainOddsSlot = (oldSlot, newSlot) => {
  if (!newSlot) return oldSlot;
  if (!oldSlot) return newSlot;
  return { ...oldSlot, ...newSlot };
};

/* ── Merge a fixture's mainOdds object slot-by-slot (see mergeMainOddsSlot)
   instead of replacing it wholesale — a live tick routinely carries only
   {oddStateId, priceDecimal, bettable, state} per slot, and a plain object
   spread at the fixture level would otherwise silently drop every other
   identity field the bet-placement payload needs. ── */
const mergeMainOdds = (oldMainOdds, newMainOdds) => {
  if (!newMainOdds) return oldMainOdds;
  if (!oldMainOdds) return newMainOdds;
  return {
    home: mergeMainOddsSlot(oldMainOdds.home, newMainOdds.home),
    draw: mergeMainOddsSlot(oldMainOdds.draw, newMainOdds.draw),
    away: mergeMainOddsSlot(oldMainOdds.away, newMainOdds.away),
  };
};

/* ── Merge an existing fixture with an incoming (possibly partial) fixture
   patch, keeping mainOdds identity fields stable across leaner ticks. ── */
const mergeFixture = (oldFixture, newFixture) => {
  const merged = { ...oldFixture, ...newFixture };
  if (newFixture.mainOdds) merged.mainOdds = mergeMainOdds(oldFixture.mainOdds, newFixture.mainOdds);
  return merged;
};

const hasJsonPatchOps = (ops) => Array.isArray(ops) && ops.some((op) => typeof op?.path === 'string');

/* ── Helper: apply a JSON Patch array (RFC 6902 subset) to an object ── */
const applyJsonPatchToObject = (target, ops = []) => {
  if (!Array.isArray(ops) || ops.length === 0) return target;
  let obj = { ...target };
  for (const op of ops) {
    const pathParts = (op.path || '').replace(/^\//, '').split('/');
    if (op.op === 'replace' || op.op === 'add') {
      let cur = obj;
      for (let i = 0; i < pathParts.length - 1; i++) {
        cur[pathParts[i]] = Array.isArray(cur[pathParts[i]])
          ? [...cur[pathParts[i]]]
          : { ...cur[pathParts[i]] };
        cur = cur[pathParts[i]];
      }
      cur[pathParts[pathParts.length - 1]] = op.value;
    } else if (op.op === 'remove') {
      let cur = obj;
      for (let i = 0; i < pathParts.length - 1; i++) {
        cur = cur[pathParts[i]];
        if (!cur) break;
      }
      if (cur) delete cur[pathParts[pathParts.length - 1]];
    }
  }
  return obj;
};

/* ── Apply a fixture-detail patch to a single fixture. Kept in this pure
   helper module so detail state and list state use the same JSON patch logic. ── */
export const applyFixturePatchToFixture = (fixture, patch) => {
  if (!fixture || !patch) return fixture;
  if (Array.isArray(patch.ops)) {
    return hasJsonPatchOps(patch.ops) ? applyJsonPatchToObject(fixture, patch.ops) : fixture;
  }
  return { ...fixture, ...patch };
};

const oddPatchId = (patch) => {
  const id = patch?.oddStateId ?? patch?.id;
  return id != null ? String(id) : null;
};

const oddItemId = (odd) => {
  const id = odd?.oddStateId ?? odd?.id;
  return id != null ? String(id) : null;
};

const normalizeOddPatchForList = (patch) => {
  const nextPrice = patch?.priceDecimal ?? patch?.odds ?? patch?.price;
  const normalized = {};
  [
    'oddStateId',
    'id',
    'marketStateId',
    'odds',
    'price',
    'bettable',
    'isBettable',
    'locked',
    'suspended',
    'is_suspended',
    'state',
    'displayState',
    'reason',
    'lockReason',
  ].forEach((key) => {
    if (patch?.[key] !== undefined) normalized[key] = patch[key];
  });
  if (nextPrice != null) normalized.priceDecimal = nextPrice;
  return normalized;
};

const mergeMainOddsWithOddPatch = (mainOdds, patch) => {
  if (!mainOdds) return { mainOdds, changed: false };
  const targetId = oddPatchId(patch);
  if (targetId == null) return { mainOdds, changed: false };

  let changed = false;
  const nextMainOdds = { ...mainOdds };
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = mainOdds[slot];
    if (oddItemId(oldSlot) !== targetId) return;
    nextMainOdds[slot] = { ...oldSlot, ...normalizeOddPatchForList(patch) };
    changed = true;
  });

  return { mainOdds: changed ? nextMainOdds : mainOdds, changed };
};

const mergeMarketsWithOddPatch = (markets, patch) => {
  if (!Array.isArray(markets)) return { markets, changed: false };
  const targetId = oddPatchId(patch);
  if (targetId == null) return { markets, changed: false };

  let anyChanged = false;
  const nextMarkets = markets.map((market) => {
    const oddsKey = market?.selections ? 'selections' : market?.odds ? 'odds' : null;
    if (!oddsKey || !Array.isArray(market[oddsKey])) return market;

    let marketChanged = false;
    const nextOdds = market[oddsKey].map((odd) => {
      if (oddItemId(odd) !== targetId) return odd;
      marketChanged = true;
      return { ...odd, ...normalizeOddPatchForList(patch) };
    });

    if (!marketChanged) return market;
    anyChanged = true;
    return { ...market, [oddsKey]: nextOdds };
  });

  return { markets: anyChanged ? nextMarkets : markets, changed: anyChanged };
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const boolOrNull = (value) => {
  if (value === true || value === false) return value;
  return null;
};

const marketStatusPatchId = (market) => {
  const id = market?.marketStateId ?? market?.market_state_id ?? market?.id ?? market?.market_id;
  return id != null ? String(id) : null;
};

const oddMarketStatusId = (odd) => {
  const id = odd?.marketStateId ?? odd?.market_state_id;
  return id != null ? String(id) : null;
};

const marketStatusTypeKey = (market) => {
  const key = market?.marketTypeKey ?? market?.market_type_key ?? market?.marketId ?? market?.market_id;
  return key != null ? String(key) : null;
};

const oddMarketTypeKey = (odd) => {
  const key = odd?.marketTypeKey ?? odd?.market_type_key ?? odd?.marketId ?? odd?.market_id;
  return key != null ? String(key) : null;
};

const marketStatusMapFrom = (markets) => {
  const map = new Map();
  (markets || []).forEach((market) => {
    const id = marketStatusPatchId(market);
    if (id != null && !map.has(id)) map.set(id, market);
  });
  return map;
};

const marketStatusBoolean = (market, keys) => {
  for (const key of keys) {
    const value = boolOrNull(market?.[key]);
    if (value !== null) return value;
    const stateValue = boolOrNull(market?.marketState?.[key]);
    if (stateValue !== null) return stateValue;
  }
  return null;
};

const marketStatusText = (market) => firstDefined(
  market?.marketState?.state,
  market?.state,
  market?.marketState?.displayState,
  market?.displayState,
  market?.marketState?.reason,
  market?.reason,
  market?.marketState?.lockReason,
  market?.lockReason,
);

const marketStatusReason = (market) => firstDefined(
  market?.marketState?.reason,
  market?.reason,
  market?.marketState?.lockReason,
  market?.lockReason,
);

const isMarketStatusLocked = (market) => {
  if (!market) return false;
  if (marketStatusBoolean(market, ['bettable', 'isBettable', 'is_bettable']) === false) return true;
  if (marketStatusBoolean(market, ['suspended', 'isSuspended', 'is_suspended']) === true) return true;
  const status = marketStatusText(market);
  return typeof status === 'string' && /lock|suspend|closed|unavailable|hidden/i.test(status);
};

const childLockPatchForMarketStatus = (market) => {
  const status = marketStatusText(market);
  const state = typeof status === 'string' && status.toUpperCase() !== 'OPEN' ? status : 'LOCKED';
  const reason = marketStatusReason(market) ?? 'MARKET_LOCKED';
  return {
    bettable: false,
    isBettable: false,
    suspended: true,
    is_suspended: true,
    locked: true,
    state,
    displayState: state,
    reason,
    lockReason: reason,
  };
};

const mergeOddWithMarketStatusLock = (odd, marketPatch) => {
  if (!isMarketStatusLocked(marketPatch)) return odd;
  return { ...odd, ...childLockPatchForMarketStatus(marketPatch) };
};

const oddMatchesMarketStatusPatch = (odd, marketPatch) => {
  const patchId = marketStatusPatchId(marketPatch);
  const oddMarketId = oddMarketStatusId(odd);
  if (patchId != null && oddMarketId != null) return patchId === oddMarketId;

  const patchType = marketStatusTypeKey(marketPatch);
  const oddType = oddMarketTypeKey(odd);
  return patchType != null && oddType != null && patchType === oddType;
};

const mergeMainOddsWithMarketStatusPatch = (mainOdds, marketPatch) => {
  if (!mainOdds || !isMarketStatusLocked(marketPatch)) return { mainOdds, changed: false };

  let changed = false;
  const nextMainOdds = { ...mainOdds };
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = mainOdds[slot];
    if (!oldSlot || !oddMatchesMarketStatusPatch(oldSlot, marketPatch)) return;
    nextMainOdds[slot] = mergeOddWithMarketStatusLock(oldSlot, marketPatch);
    changed = true;
  });

  return { mainOdds: changed ? nextMainOdds : mainOdds, changed };
};

const mergeMarketWithStatusPatch = (market, marketPatch, flashEvents = null) => {
  const nextBettable = marketStatusBoolean(marketPatch, ['bettable', 'isBettable', 'is_bettable']);
  const nextSuspended = marketStatusBoolean(marketPatch, ['suspended', 'isSuspended', 'is_suspended']);
  const key = market?.selections ? 'selections' : market?.odds ? 'odds' : null;

  let changed = false;
  let nextMarket = {
    ...market,
    ...(nextBettable !== null && { bettable: nextBettable, isBettable: nextBettable }),
    ...(nextSuspended !== null && { suspended: nextSuspended, is_suspended: nextSuspended }),
    ...(marketPatch.marketState !== undefined && { marketState: marketPatch.marketState }),
  };

  changed = nextMarket !== market;

  if (key && Array.isArray(market[key]) && isMarketStatusLocked(marketPatch)) {
    const nextItems = market[key].map((odd) => {
      const lockedOdd = mergeOddWithMarketStatusLock(odd, marketPatch);
      if (flashEvents && odd.oddStateId != null) {
        const direction = resolveOddFlash(odd, lockedOdd);
        if (direction) flashEvents.push({ oddStateId: odd.oddStateId, direction });
      }
      if (lockedOdd !== odd) changed = true;
      return lockedOdd;
    });
    nextMarket = { ...nextMarket, [key]: nextItems };
  }

  return { market: changed ? nextMarket : market, changed };
};

const mergeMarketsWithMarketStatusPatch = (markets, marketPatch, flashEvents = null) => {
  if (!Array.isArray(markets)) return { markets, changed: false };
  const targetId = marketStatusPatchId(marketPatch);
  if (targetId == null) return { markets, changed: false };

  let anyChanged = false;
  const nextMarkets = markets.map((market) => {
    if (marketStatusPatchId(market) !== targetId) return market;
    const result = mergeMarketWithStatusPatch(market, marketPatch, flashEvents);
    if (result.changed) anyChanged = true;
    return result.market;
  });

  return { markets: anyChanged ? nextMarkets : markets, changed: anyChanged };
};

const statusOnlyMarketPatchesFromFixturePatch = (patch) => {
  if (!Array.isArray(patch?.markets)) return [];
  return patch.markets.filter((market) => (market?.selections ?? market?.odds ?? []).length === 0);
};

const applyMarketStatusPatchToFixture = (fixture, marketPatch) => {
  const mainResult = mergeMainOddsWithMarketStatusPatch(fixture.mainOdds, marketPatch);
  const marketsResult = mergeMarketsWithMarketStatusPatch(fixture.markets, marketPatch);

  if (!mainResult.changed && !marketsResult.changed) return fixture;
  return {
    ...fixture,
    ...(mainResult.changed && { mainOdds: mainResult.mainOdds }),
    ...(marketsResult.changed && { markets: marketsResult.markets }),
  };
};

/* ── Apply a selected/odd patch to the list state. selected:patch updates can
   be the only event a visible list cell receives; oddsFlash is keyed globally
   by oddStateId, but the list card renders from fixture.mainOdds (and some
   domestic rows from fixture.markets). Keep both copies in sync so cells do
   not flash while still showing stale price/lock state until refresh. ── */
export const applyOddPatchToFixtureList = (fixtures, patch) => {
  if (!Array.isArray(fixtures) || oddPatchId(patch) == null) return fixtures;

  let anyChanged = false;
  const nextFixtures = fixtures.map((fixture) => {
    const mainResult = mergeMainOddsWithOddPatch(fixture.mainOdds, patch);
    const marketsResult = mergeMarketsWithOddPatch(fixture.markets, patch);

    if (!mainResult.changed && !marketsResult.changed) return fixture;
    anyChanged = true;
    return {
      ...fixture,
      ...(mainResult.changed && { mainOdds: mainResult.mainOdds }),
      ...(marketsResult.changed && { markets: marketsResult.markets }),
    };
  });

  return anyChanged ? nextFixtures : fixtures;
};

const fixturePatchId = (patch, fallbackFixtureId = null) => {
  const id =
    patch?.fixtureId ??
    patch?.fixture_id ??
    patch?.id ??
    patch?.fixture?.fixtureId ??
    patch?.fixture?.fixture_id ??
    patch?.fixture?.id ??
    fallbackFixtureId;
  return id != null ? String(id) : null;
};

const fixtureArrayFromPatchFixtures = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    return Object.values(value).filter((item) => item && typeof item === 'object');
  }
  return [];
};

const FIXTURE_LIST_PATCH_FIELDS = [
  'fixtureId',
  'fixture_id',
  'id',
  'sport',
  'sportSlug',
  'sportKey',
  'leagueId',
  'league_id',
  'leagueName',
  'leagueNameKo',
  'league',
  'home',
  'away',
  'homeLogo',
  'awayLogo',
  'startsAt',
  'startTime',
  'starts_at',
  'status',
  'period',
  'periodNumber',
  'clock',
  'score',
  'live_score_home',
  'live_score_away',
  'score_home',
  'score_away',
  'scoreHome',
  'scoreAway',
  'marketCount',
  'marketState',
  'mainOdds',
  'mainOddsStatus',
  'updatedAt',
  'stale',
  'recovery',
  'rowOrder',
  'row_order',
  'esports',
];

const fixtureListFieldPatch = (patch) => {
  if (patch?.fixture) return patch.fixture;
  const fieldPatch = {};
  FIXTURE_LIST_PATCH_FIELDS.forEach((key) => {
    if (patch?.[key] !== undefined) fieldPatch[key] = patch[key];
  });
  return Object.keys(fieldPatch).length > 0 ? fieldPatch : null;
};

const oddPatchesFromFixturePatch = (patch) => {
  const odds = [];
  if (Array.isArray(patch?.markets)) {
    patch.markets.forEach((market) => {
      const items = market?.selections ?? market?.odds ?? [];
      if (Array.isArray(items)) odds.push(...items);
    });
  }
  if (Array.isArray(patch?.odds)) odds.push(...patch.odds);
  if (patch?.oddStateId != null) odds.push(patch);
  return odds;
};

/* ── Apply a fixture-room patch to the list state as well. The open detail
   room can be the only stream carrying a price/lock/status change for the
   visible fixture; oddsFlash is global, so the list cell may blink even while
   fixture.mainOdds remains stale unless this patch also updates fixtures[]. ── */
export const applyFixturePatchToFixtureList = (fixtures, patch, fallbackFixtureId = null) => {
  if (!Array.isArray(fixtures) || !patch) return fixtures;
  const targetId = fixturePatchId(patch, fallbackFixtureId);
  let nextFixtures = fixtures;

  if (targetId != null) {
    let metadataChanged = false;
    const fieldPatch = fixtureListFieldPatch(patch);
    nextFixtures = nextFixtures.map((fixture) => {
      if (fxId(fixture) !== targetId) return fixture;

      let nextFixture = fixture;
      if (hasJsonPatchOps(patch.ops)) nextFixture = applyJsonPatchToObject(nextFixture, patch.ops);
      if (fieldPatch) nextFixture = mergeFixture(nextFixture, fieldPatch);

      if (nextFixture !== fixture) metadataChanged = true;
      return nextFixture;
    });
    if (!metadataChanged) nextFixtures = fixtures;
  }

  oddPatchesFromFixturePatch(patch).forEach((oddPatch) => {
    nextFixtures = applyOddPatchToFixtureList(nextFixtures, oddPatch);
  });

  statusOnlyMarketPatchesFromFixturePatch(patch).forEach((marketPatch) => {
    nextFixtures = nextFixtures.map((fixture) => {
      if (targetId != null && fxId(fixture) !== targetId) return fixture;
      return applyMarketStatusPatchToFixture(fixture, marketPatch);
    });
  });

  return nextFixtures;
};

/* ── Apply a list patch to a fixtures array ── */
export const applyListPatch = (fixtures, patch) => {
  if (!patch) return fixtures;

  // "Full replace" shape — but confirmed via debug logging that this event does NOT
  // reliably carry a full room snapshot: it is routinely a single recently-changed
  // fixture reusing the same `{ fixtures: [...] }` envelope (observed: every tick
  // carries exactly 1 fixture, rotating between unrelated matches of different
  // sports). Treating it as authoritative and pruning anything absent from it wiped
  // out every other live fixture on nearly every tick. Always upsert-merge instead —
  // never prune here. Removal only happens via an explicit ops:remove (below) or a
  // fresh REST fetch (sport/league/page change).
  //
  // UPDATE existing fixtures only — never INSERT a brand-new fixtureId here.
  // The WS list room this subscribes to is broader than "just this page"
  // (e.g. every league under a mode+sport), so treating an unseen fixtureId
  // as an insert made a fixed-size page (e.g. 30 items) silently grow without
  // bound as unrelated matches elsewhere in the room ticked in — this is what
  // looked like the match list "infinitely loading" while scrolling, with no
  // REST call involved at all. A genuinely new match should only become
  // visible via an explicit refresh, page change, or filter change.
  const patchFixtures = fixtureArrayFromPatchFixtures(patch.fixtures);
  if (patchFixtures.length > 0) {
    const inMap = new Map();
    for (const f of patchFixtures) {
      const id = fxId(f);
      if (id != null && !inMap.has(id)) inMap.set(id, f);
    }
    const incoming = Array.from(inMap.values());

    const map = new Map(fixtures.map((f) => [fxId(f), f]));
    incoming.forEach((f) => {
      const id = fxId(f);
      if (!map.has(id)) return;
      map.set(id, mergeFixture(map.get(id), f));
    });
    return Array.from(map.values());
  }

  // Incremental ops: upsert / remove individual fixture cards.
  // Backend sends the op kind as `op.op`, not `op.type` — accept both so either
  // contract works instead of silently dropping every op.
  if (Array.isArray(patch.ops)) {
    const map = new Map(fixtures.map((f) => [fxId(f), f]));
    for (const op of patch.ops) {
      const kind = op.op ?? op.type;
      if (kind === 'upsert' && op.fixture) {
        // Same reasoning as the patch.fixtures branch above — update only,
        // never insert a fixture this page hasn't already shown.
        const id = fxId(op.fixture);
        if (id != null && map.has(id)) map.set(id, mergeFixture(map.get(id), op.fixture));
      } else if (kind === 'remove') {
        const id = fxId(op);
        if (id != null) map.delete(id);
      }
    }
    return Array.from(map.values());
  }

  // Odds / status field patches on individual fixtures.
  // Backend sends `fields` as an array of changed field NAMES (e.g. ['mainOdds']),
  // with the actual updated values as sibling top-level keys on `patch` itself
  // (e.g. patch.mainOdds) — not nested inside `patch.fields`. Still accept the
  // older object-shaped `fields` (name → value) as a fallback.
  if (patch.fixtureId != null && patch.fields) {
    const fieldNames = Array.isArray(patch.fields) ? patch.fields : Object.keys(patch.fields);
    const fieldData = {};
    fieldNames.forEach((name) => {
      if (patch[name] !== undefined) fieldData[name] = patch[name];
      else if (!Array.isArray(patch.fields) && patch.fields[name] !== undefined) fieldData[name] = patch.fields[name];
    });
    return fixtures.map((f) => {
      if (fxId(f) !== String(patch.fixtureId)) return f;
      const merged = { ...f, ...fieldData };
      if (fieldData.mainOdds) merged.mainOdds = mergeMainOdds(f.mainOdds, fieldData.mainOdds);
      return merged;
    });
  }

  return fixtures;
};

/* ── Whether an odd is currently locked/unbettable, regardless of which of
   the several field names a given payload shape uses for it (all confirmed
   present across real captured list/detail/selected patches). ── */
export const isOddLocked = (odd) => {
  if (!odd) return false;
  if (odd.bettable === false) return true;
  if (odd.isBettable === false) return true;
  if (odd.suspended === true) return true;
  if (odd.is_suspended === true) return true;
  if (odd.locked === true) return true;
  if (typeof odd.state === 'string' && /suspend|lock|closed|inactive/i.test(odd.state)) return true;
  if (typeof odd.displayState === 'string' && /suspend|lock|closed|inactive/i.test(odd.displayState)) return true;
  return false;
};

/* ── Resolve the flash direction between an odd's old and new state: a price
   move takes priority (up/down); otherwise a bettable/suspended/locked/
   displayState transition still flashes ('locked' on lock, 'unlocked' on
   reopen) so a lock/suspend is never silently invisible just because the
   price didn't move. Returns null when neither price nor lock-state changed. ── */
export const resolveOddFlash = (oldOdd, newOdd) => {
  if (!oldOdd || !newOdd) return null;
  const oldPrice = oldOdd.priceDecimal != null ? Number(oldOdd.priceDecimal) : null;
  const newPrice = newOdd.priceDecimal != null ? Number(newOdd.priceDecimal) : null;
  if (oldPrice != null && newPrice != null && oldPrice !== newPrice) {
    return newPrice > oldPrice ? 'up' : 'down';
  }
  const wasLocked = isOddLocked(oldOdd);
  const nowLocked = isOddLocked(newOdd);
  if (wasLocked !== nowLocked) return nowLocked ? 'locked' : 'unlocked';
  return null;
};

/* ── Compare a fixture's old/new mainOdds (home/draw/away) and return the
   slots whose price OR lock/unlock state actually changed, with the flash
   direction ── */
export const diffMainOdds = (oldMainOdds, newMainOdds) => {
  const changes = [];
  if (!oldMainOdds || !newMainOdds) return changes;
  ['home', 'draw', 'away'].forEach((slot) => {
    const oldSlot = oldMainOdds[slot];
    const newSlot = newMainOdds[slot];
    if (!oldSlot?.oddStateId || !newSlot) return;
    const direction = resolveOddFlash(oldSlot, newSlot);
    if (direction) changes.push({ oddStateId: oldSlot.oddStateId, direction });
  });
  return changes;
};

/* ── Given a raw sportsnew:list:patch payload and the currently-known
   fixtures, resolve the { oldMainOdds, newMainOdds } pairs that need
   diffing for a flash. Confirmed via a captured real payload: the updated
   mainOdds lives inside each item of `patch.fixtures[]` — a single-item
   array per changed fixture, always a COMPLETE fixture object — NOT on a
   top-level `patch.mainOdds` sibling, and NOT inside `patch.ops[].fixture`
   (the real ops there are lightweight markers — {op, fixtureId, mode, sport}
   — with no fixture data at all, even when `patch.fixtures` is present in
   the very same message). `patch.fixtures` is the authoritative source; the
   other two shapes are kept only as defensive fallbacks for any backend
   variant that might send odds updates differently. ── */
export const resolveMainOddsUpdatesFromListPatch = (patch, oldFixtures) => {
  const updates = [];
  const findOld = (id) => oldFixtures.find((f) => fxId(f) === id);

  if (Array.isArray(patch?.fixtures)) {
    patch.fixtures.forEach((newFixture) => {
      const oldFixture = findOld(fxId(newFixture));
      if (oldFixture?.mainOdds && newFixture?.mainOdds) {
        updates.push({ oldMainOdds: oldFixture.mainOdds, newMainOdds: newFixture.mainOdds });
      }
    });
  } else if (patch?.fixtureId != null) {
    // Field-only patch shape (no `fixtures` array): backend sends changed-field
    // NAMES in `fields` (array, e.g. ['mainOdds']) with the actual new values as
    // sibling top-level keys on `patch` itself (patch.mainOdds). Fall back to the
    // older object-shaped `fields` for safety.
    const fieldsList = Array.isArray(patch?.fields) ? patch.fields : null;
    const hasMainOddsField = fieldsList ? fieldsList.includes('mainOdds') : !!patch?.fields?.mainOdds;
    if (hasMainOddsField) {
      const oldFixture = findOld(String(patch.fixtureId));
      const newOdds = patch.mainOdds ?? (fieldsList ? null : patch.fields?.mainOdds);
      if (oldFixture?.mainOdds && newOdds) updates.push({ oldMainOdds: oldFixture.mainOdds, newMainOdds: newOdds });
    }
  }

  if (Array.isArray(patch?.ops)) {
    // Some backend variants may carry updated mainOdds directly on the op's own
    // fixture payload. Backend sends the op kind as `op.op`, not `op.type`.
    // Independent of the branches above since a message can carry `fixtures`
    // and `ops` together (as the real captured payload does).
    for (const op of patch.ops) {
      const kind = op.op ?? op.type;
      if (kind === 'upsert' && op.fixture?.mainOdds) {
        const oldFixture = findOld(fxId(op.fixture));
        if (oldFixture?.mainOdds) updates.push({ oldMainOdds: oldFixture.mainOdds, newMainOdds: op.fixture.mainOdds });
      }
    }
  }

  return updates;
};

/* ── If this list-patch's fixture is the one currently open in the detail
   panel, push its fresh mainOdds prices into the detail panel's own markets
   array. Confirmed via captured live traffic: sportsnew:fixture:patch /
   selected:patch do not reliably fire for a plain moneyline tick — mainOdds
   updates flow almost exclusively through this list-patch stream, even for
   whichever fixture's detail panel happens to be open. Without this, the
   "메인"/머니라인 row in the detail panel never refreshes even though the list
   price and its flash both do. Returns the same `detailMarkets` reference
   unchanged when there's nothing to update. ── */
export const applyMainOddsToDetailMarkets = (patch, openFixtureId, detailMarkets) => {
  if (!Array.isArray(patch?.fixtures) || openFixtureId == null) return detailMarkets;
  const openFixture = patch.fixtures.find((f) => fxId(f) === String(openFixtureId));
  if (!openFixture?.mainOdds) return detailMarkets;

  const updateById = new Map();
  ['home', 'draw', 'away'].forEach((slot) => {
    const o = openFixture.mainOdds[slot];
    if (o?.oddStateId != null) updateById.set(o.oddStateId, o);
  });
  if (updateById.size === 0) return detailMarkets;

  return detailMarkets.map((mkt) => {
    const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
    if (!key) return mkt;
    let changed = false;
    const items = mkt[key].map((o) => {
      const upd = updateById.get(o.oddStateId);
      if (!upd) return o;
      changed = true;
      return { ...o, priceDecimal: upd.priceDecimal, bettable: upd.bettable, state: upd.state };
    });
    return changed ? { ...mkt, [key]: items } : mkt;
  });
};

const marketFamilyOf = (m) => m?.marketFamily ?? m?.marketCategoryKey ?? null;

/* ── Stable identity key for a MARKET (not a selection/odd within it) — a
   single marketStateId can legitimately be reused across multiple DISTINCT
   markets (different lines, periods, or team-scoped variants), so keying/
   deduping/grouping by marketStateId ALONE collapses them into one, which is
   exactly why "markets with multiple lines only show one". Prefers the API's
   own marketGroupId (the single most precise identity available); falls
   back to a composite of every dimension that can distinguish two markets
   sharing a marketStateId: family, period, line, team side, and
   selection-set shape. ── */
export const marketIdentityKey = (market) => {
  if (!market) return null;
  if (market.marketGroupId != null) return `mg:${market.marketGroupId}`;

  const marketStateId = market.marketStateId ?? market.id ?? market.market_id ?? null;
  const marketFamily = marketFamilyOf(market);
  const periodScope = market.periodScope ?? null;
  const lineKey = market.canonicalLineKey ?? market.lineKey ?? market.line ?? market.points ?? null;
  const teamSide = market.teamSide ?? market.team ?? null;
  const selectionSetKey = market.selectionSetKey ?? market.selectionSetId ?? null;

  const parts = [marketStateId, marketFamily, periodScope, lineKey, teamSide, selectionSetKey];
  // No identity dimension available at all — never collapse two such
  // markets against each other (matches the prior "unnamed" bucket
  // behavior for a market with no marketStateId).
  if (parts.every((p) => p == null)) return null;

  return ['fb', ...parts.map((p) => String(p ?? ''))].join('|');
};

/* ── Insert a genuinely NEW oddStateId (one not already present in any
   market's selections/odds) into the market/line group it belongs to.
   detail-panel fixture patches (`patch.odds[]` / a single top-level
   `patch.oddStateId`) previously only ever UPDATED an existing odd in place —
   a brand-new oddStateId (a line just being added, e.g. a fresh total/
   handicap line opening) was silently invisible until the next full page
   reload.
   Never places by marketStateId alone — multiple DISTINCT markets (lines/
   periods/team-scoped variants) can share one marketStateId. Priority:
   1) the odd's own marketGroupId, if present — most precise.
   2) marketStateId, narrowed by family/period/line/teamSide when more than
      one market shares that marketStateId.
   3) family/periodScope alone, when the odd carries no marketStateId at all.
   Any remaining ambiguity (more than one candidate, or none) returns
   inserted:false so the caller can recover instead of guessing wrong. ── */
export const insertOddIntoMarkets = (markets, odd) => {
  if (!Array.isArray(markets) || !odd || odd.oddStateId == null) {
    return { markets, inserted: false };
  }

  const alreadyPresent = markets.some((m) =>
    (m.selections ?? m.odds ?? []).some((o) => o.oddStateId === odd.oddStateId)
  );
  if (alreadyPresent) return { markets, inserted: false };

  let targetIndex = -1;

  if (odd.marketGroupId != null) {
    targetIndex = markets.findIndex((m) => m.marketGroupId != null && String(m.marketGroupId) === String(odd.marketGroupId));
  }

  if (targetIndex === -1) {
    const targetMarketStateId = odd.marketStateId ?? odd.marketId ?? null;
    if (targetMarketStateId != null) {
      const candidates = [];
      markets.forEach((m, i) => {
        if ((m.marketStateId ?? m.id ?? m.market_id) === targetMarketStateId) candidates.push(i);
      });

      if (candidates.length === 1) {
        targetIndex = candidates[0];
      } else if (candidates.length > 1) {
        // Multiple markets share this marketStateId — narrow using whichever
        // of the odd's own identity fields are present. Never guess: if
        // narrowing still leaves more than one (or filters out all of them),
        // leave targetIndex unset.
        const oddFamily = odd.marketFamily ?? odd.marketCategoryKey ?? null;
        const oddPeriod = odd.periodScope ?? null;
        const oddLine = odd.canonicalLineKey ?? odd.lineKey ?? (odd.line != null ? String(odd.line) : null);
        const oddTeam = odd.teamSide ?? odd.team ?? null;

        const narrowed = candidates.filter((i) => {
          const m = markets[i];
          const familyOk = oddFamily == null || marketFamilyOf(m) === oddFamily;
          const periodOk = oddPeriod == null || (m.periodScope ?? null) === oddPeriod;
          const lineOk = oddLine == null || String(m.canonicalLineKey ?? m.lineKey ?? m.line ?? '') === String(oddLine);
          const teamOk = oddTeam == null || (m.teamSide ?? m.team ?? null) === oddTeam;
          return familyOk && periodOk && lineOk && teamOk;
        });
        if (narrowed.length === 1) targetIndex = narrowed[0];
      }
    } else {
      const oddFamily = odd.marketFamily ?? odd.marketCategoryKey ?? null;
      if (oddFamily != null) {
        const oddPeriod = odd.periodScope ?? null;
        const candidates = [];
        markets.forEach((m, i) => {
          if (marketFamilyOf(m) === oddFamily && (m.periodScope ?? null) === oddPeriod) candidates.push(i);
        });
        // Exactly one candidate market — safe to place it there. More than one
        // (e.g. two markets sharing the same family/period) is ambiguous: we
        // cannot know which one this line belongs to, so don't guess.
        if (candidates.length === 1) targetIndex = candidates[0];
      }
    }
  }

  if (targetIndex === -1) return { markets, inserted: false };

  const key = markets[targetIndex].selections ? 'selections' : 'odds';
  const nextMarkets = markets.slice();
  nextMarkets[targetIndex] = {
    ...markets[targetIndex],
    [key]: [...(markets[targetIndex][key] ?? []), odd],
  };
  return { markets: nextMarkets, inserted: true };
};

/* ── Deduplicate markets by stable identity (see marketIdentityKey — never
   marketStateId alone, since it can be reused across distinct lines/periods/
   team-scoped variants) and odds within each by oddStateId. ── */
export const dedupMarkets = (markets) => {
  if (!Array.isArray(markets)) return markets;
  const mktMap = new Map();
  const unkeyed = [];
  for (const mkt of markets) {
    const key = marketIdentityKey(mkt);
    if (key != null) {
      if (!mktMap.has(key)) mktMap.set(key, mkt);
    } else {
      unkeyed.push(mkt);
    }
  }
  return [...Array.from(mktMap.values()), ...unkeyed].map((mkt) => {
    const selKey = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
    if (!selKey) return mkt;
    const oddMap = new Map();
    const unnamedOdds = [];
    for (const odd of mkt[selKey]) {
      const oid = odd.oddStateId;
      if (oid != null) {
        if (!oddMap.has(oid)) oddMap.set(oid, odd);
      } else {
        unnamedOdds.push(odd);
      }
    }
    return { ...mkt, [selKey]: [...Array.from(oddMap.values()), ...unnamedOdds] };
  });
};

const marketPatchItems = (market) => market?.selections ?? market?.odds ?? [];

const patchMarketArray = (patch) => (
  Array.isArray(patch?.marketSnapshot) ? patch.marketSnapshot : patch?.markets
);

const isExplicitFullMarketProjection = (patch) => (
  Array.isArray(patch?.marketSnapshot) ||
  patch?.isFullFixtureProjection === true ||
  patch?.is_full_fixture_projection === true ||
  String(patch?.projectionScope ?? patch?.projection_scope ?? '').toLowerCase() === 'full' ||
  patch?.replaceReason != null ||
  patch?.replace_reason != null
);

const detailMarketMergeKey = (market) => {
  const key = marketIdentityKey(market);
  if (key != null) return key;
  const id = marketStatusPatchId(market);
  return id != null ? `state:${id}` : null;
};

const marketItemKey = (incomingMarket, existingMarket) => {
  if (incomingMarket?.selections) return 'selections';
  if (incomingMarket?.odds) return 'odds';
  if (existingMarket?.selections) return 'selections';
  if (existingMarket?.odds) return 'odds';
  return null;
};

const mergeMarketItemsPreservingExisting = (existingMarket, incomingMarket) => {
  const itemKey = marketItemKey(incomingMarket, existingMarket);
  if (!itemKey) return { ...existingMarket, ...incomingMarket };

  const existingItems = Array.isArray(existingMarket?.[itemKey]) ? existingMarket[itemKey] : [];
  const incomingItems = Array.isArray(incomingMarket?.[itemKey]) ? incomingMarket[itemKey] : [];
  const mergedById = new Map();
  const unnamedItems = [];

  existingItems.forEach((item) => {
    const id = item?.oddStateId ?? item?.id;
    if (id != null) mergedById.set(String(id), item);
    else unnamedItems.push(item);
  });
  incomingItems.forEach((item) => {
    const id = item?.oddStateId ?? item?.id;
    if (id != null) {
      const previous = mergedById.get(String(id));
      mergedById.set(String(id), previous ? { ...previous, ...item } : item);
    } else {
      unnamedItems.push(item);
    }
  });

  return {
    ...existingMarket,
    ...incomingMarket,
    [itemKey]: [...Array.from(mergedById.values()), ...unnamedItems],
  };
};

const mergePartialMarketSnapshots = (markets, incomingMarkets, flashEvents, markRecovery) => {
  let nextMarkets = Array.isArray(markets) ? markets.slice() : [];
  const oldOddsMap = new Map();
  nextMarkets.forEach((mkt) => marketPatchItems(mkt).forEach((o) => {
    if (o.oddStateId != null) oldOddsMap.set(o.oddStateId, o);
  }));

  incomingMarkets.forEach((incomingMarket) => {
    marketPatchItems(incomingMarket).forEach((o) => {
      if (o.oddStateId == null) return;
      const direction = resolveOddFlash(oldOddsMap.get(o.oddStateId), o);
      if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
    });

    const incomingKey = detailMarketMergeKey(incomingMarket);
    if (incomingKey == null) {
      nextMarkets.push(incomingMarket);
      markRecovery();
      return;
    }

    const existingIndex = nextMarkets.findIndex((market) => detailMarketMergeKey(market) === incomingKey);
    if (existingIndex === -1) {
      nextMarkets.push(incomingMarket);
      return;
    }

    nextMarkets[existingIndex] = mergeMarketItemsPreservingExisting(nextMarkets[existingIndex], incomingMarket);
  });

  return dedupMarkets(nextMarkets);
};

/**
 * Merge a sportsnew:fixture:patch's markets[] AND odds[] into the current
 * detailMarkets array. These are INDEPENDENT concerns that can BOTH be
 * present, non-empty, in the SAME real payload — a prior if/else-if between
 * them in SportsnewContext.jsx silently dropped whichever one came second.
 * Both are always processed here, in order: markets[] first (explicit full
 * projection replaces the tree; otherwise renderable market patches upsert
 * without pruning siblings, and status-stubs merge bettable/suspended), then odds[] on top of that result (update
 * existing oddStateIds, or insert/upsert a new one when identity is
 * sufficient — see insertOddIntoMarkets). A lone top-level `oddStateId` is
 * an alternate SHAPE for the same "odds changed" concept as odds[] (never
 * both present in one real payload) but is still independent of markets[].
 *
 * Pure — returns the next markets array plus flash events and a recovery
 * signal as plain data; the caller (SportsnewContext) is responsible for the
 * actual flashOdds/setDetailMarkets/recoverFixture side effects.
 */
export const applyFixtureDetailPatch = (prevMarkets, patch) => {
  let workingMarkets = Array.isArray(prevMarkets) ? prevMarkets : [];
  const flashEvents = [];
  let needsRecovery = false;

  const incomingMarkets = patchMarketArray(patch);
  if (Array.isArray(incomingMarkets)) {
    const patchHasOdds = incomingMarkets.some(
      (m) => (m.selections ?? m.odds ?? []).length > 0
    );
    if (patchHasOdds && isExplicitFullMarketProjection(patch)) {
      // Diff against what's currently shown BEFORE it's overwritten, so
      // every changed price OR lock/unlock transition flashes, not just the
      // ones a separate top-level oddStateId/odds[] field happens to call out.
      const oldOddsMap = new Map();
      workingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
        if (o.oddStateId != null) oldOddsMap.set(o.oddStateId, o);
      }));
      incomingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
        if (o.oddStateId == null) return;
        const direction = resolveOddFlash(oldOddsMap.get(o.oddStateId), o);
        if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
      }));
      workingMarkets = dedupMarkets(incomingMarkets);
    } else if (patchHasOdds) {
      workingMarkets = mergePartialMarketSnapshots(
        workingMarkets,
        incomingMarkets,
        flashEvents,
        () => { needsRecovery = true; }
      );
    } else {
      // Status-only stubs — merge bettable/suspended into existing markets.
      // If a live push announces a market that the REST-loaded detail panel has
      // never seen, this stub has no selections to render; trigger the existing
      // fixture recovery path so the full market/selection snapshot is fetched.
      const knownMarketStateIds = new Set(
        workingMarkets
          .map((mkt) => mkt?.marketStateId ?? mkt?.id ?? mkt?.market_id)
          .filter((id) => id != null)
          .map(String)
      );
      const hasUnknownMarket = incomingMarkets.some((mkt) => {
        const id = mkt?.marketStateId ?? mkt?.id ?? mkt?.market_id;
        return id != null && !knownMarketStateIds.has(String(id));
      });
      if (hasUnknownMarket) needsRecovery = true;

      const statusMap = marketStatusMapFrom(incomingMarkets);
      workingMarkets = workingMarkets.map((mkt) => {
        const upd = statusMap.get(marketStatusPatchId(mkt));
        if (!upd) return mkt;
        return mergeMarketWithStatusPatch(mkt, upd, flashEvents).market;
      });
    }
  }

  if (Array.isArray(patch?.odds)) {
    // Bulk changed-odds array — processed independently of patch.markets[]
    // above, on top of workingMarkets (the just-applied markets[] result if
    // that ran above, otherwise the previous detailMarkets untouched).
    const prevOddsMap = new Map();
    workingMarkets.forEach((mkt) => (mkt.selections ?? mkt.odds ?? []).forEach((o) => {
      if (o.oddStateId != null) prevOddsMap.set(o.oddStateId, o);
    }));

    const updById = new Map();
    patch.odds.forEach((od) => {
      const oid = od.oddStateId ?? od.id;
      if (oid != null) updById.set(oid, od);
    });

    let nextMarkets = workingMarkets.map((mkt) => {
      const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
      if (!key) return mkt;
      let changed = false;
      const items = mkt[key].map((o) => {
        const upd = updById.get(o.oddStateId);
        if (!upd || !prevOddsMap.has(o.oddStateId)) return o;
        changed = true;
        const merged = { ...o, ...upd };
        const direction = resolveOddFlash(o, merged);
        if (direction) flashEvents.push({ oddStateId: o.oddStateId, direction });
        return merged;
      });
      return changed ? { ...mkt, [key]: items } : mkt;
    });

    // Any oddStateId in the patch that ISN'T an update to an existing
    // selection is a genuinely new one — insert it into its market/line
    // group, or flag a recovery if we can't place it. Never silently ignore
    // it (that previously left a newly-opened line invisible).
    updById.forEach((od, oid) => {
      if (prevOddsMap.has(oid)) return;
      const { markets: withInsert, inserted } = insertOddIntoMarkets(nextMarkets, { ...od, oddStateId: oid });
      if (inserted) nextMarkets = withInsert;
      else needsRecovery = true;
    });

    workingMarkets = nextMarkets;
  } else if (patch?.oddStateId != null) {
    // Individual odd update — patch in-place and flash price OR lock/unlock
    // transitions; if this oddStateId isn't known yet, it's an insert, not
    // an update (see insertOddIntoMarkets).
    let oldOdd = null;
    for (const mkt of workingMarkets) {
      const items = mkt.selections ?? mkt.odds ?? [];
      const found = items.find((o) => o.oddStateId === patch.oddStateId);
      if (found) { oldOdd = found; break; }
    }

    if (oldOdd) {
      const merged = { ...oldOdd, ...patch };
      const direction = resolveOddFlash(oldOdd, merged);
      if (direction) flashEvents.push({ oddStateId: patch.oddStateId, direction });
      workingMarkets = workingMarkets.map((mkt) => {
        const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
        if (!key) return mkt;
        if (!mkt[key].some((o) => o.oddStateId === patch.oddStateId)) return mkt;
        return { ...mkt, [key]: mkt[key].map((o) => o.oddStateId === patch.oddStateId ? { ...o, ...patch } : o) };
      });
    } else {
      const { markets: withInsert, inserted } = insertOddIntoMarkets(workingMarkets, patch);
      if (inserted) workingMarkets = withInsert;
      else needsRecovery = true;
    }
  }

  return { markets: workingMarkets, flashEvents, needsRecovery };
};
